import React from "react";
import TourBase from "@tiny/tour";
import type { TourDefinition } from "@tiny/tour";
import { IconButton, Menu, MenuItem, Tooltip, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { HelpCircleOutline } from "@tiny/icons";
import { useJwtToken } from "@/hooks/useJwtToken";
import { useTourContext } from "./TourContext";

interface TourProps {
  tour: TourDefinition;
  enabled?: boolean;
  helpCenterUrl?: string;
  onStepAfter?: (index: number) => void;
}

function Tour({ tour, enabled = true, helpCenterUrl, onStepAfter }: TourProps) {
  const { token } = useJwtToken();
  const { setRestartTour } = useTourContext();
  const theme = useTheme();

  const [startTour, setStartTourFn] = React.useState<(() => void) | null>(null);
  const [helpAnchor, setHelpAnchor] = React.useState<null | HTMLElement>(null);

  const handleRegisterRestart = React.useCallback(
    (fn: (() => void) | null) => {
      setStartTourFn(fn ? () => fn : null);
      setRestartTour(fn);
    },
    [setRestartTour]
  );

  const handleComplete = React.useCallback(() => {
    enqueueSnackbar("Tour complete! You know your way around.", {
      variant: "success",
    });
  }, []);

  return (
    <>
      <TourBase
        tour={tour}
        enabled={enabled}
        accountId={token?.accountId}
        primaryColor={theme.palette.primary.main}
        onStepAfter={onStepAfter}
        onComplete={handleComplete}
        onRegisterRestart={handleRegisterRestart}
      />
      {(startTour || helpCenterUrl) && (
        <>
          <Tooltip title="Help" placement="left">
            <IconButton
              size="small"
              onClick={(e) => setHelpAnchor(e.currentTarget)}
              sx={{
                position: "fixed",
                top: 80,
                right: 24,
                zIndex: 9998,
                backgroundColor: "white",
                border: "1px solid #E7E9EE",
                boxShadow: "0 2px 8px rgba(15, 21, 38, 0.12)",
                "&:hover": { backgroundColor: "#F4F6FB" },
              }}
            >
              <HelpCircleOutline />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={helpAnchor}
            open={Boolean(helpAnchor)}
            onClose={() => setHelpAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {startTour && (
              <MenuItem
                onClick={() => {
                  setHelpAnchor(null);
                  startTour();
                }}
              >
                Take a Tour
              </MenuItem>
            )}
            {helpCenterUrl && (
              <MenuItem
                component="a"
                href={helpCenterUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setHelpAnchor(null)}
              >
                Help Center
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </>
  );
}

export default Tour;
